import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const PlanContext = createContext({
  billing: null,
  loading: false,
  refreshBilling: async () => {},
  openUpgradeModal: () => {},
  closeUpgradeModal: () => {},
  upgradeModal: { open: false, payload: null },
});

export function PlanProvider({ children }) {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState({ open: false, payload: null });

  const refreshBilling = useCallback(async () => {
    const adminEmail =
      sessionStorage.getItem("adminEmail") ||
      sessionStorage.getItem("userEmail");
    if (!adminEmail) return null;
    setLoading(true);
    try {
      const res = await fetch(`/api/billing/me/${encodeURIComponent(adminEmail)}`);
      if (!res.ok) throw new Error("Failed to load billing");
      const data = await res.json();
      setBilling(data);
      return data;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const openUpgradeModal = useCallback((payload = null) => {
    setUpgradeModal({ open: true, payload });
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setUpgradeModal({ open: false, payload: null });
  }, []);

  useEffect(() => {
    refreshBilling();
  }, [refreshBilling]);

  useEffect(() => {
    const onLimit = (e) => openUpgradeModal(e.detail || null);
    window.addEventListener("zordigo-plan-limit", onLimit);
    return () => window.removeEventListener("zordigo-plan-limit", onLimit);
  }, [openUpgradeModal]);

  const value = useMemo(
    () => ({
      billing,
      loading,
      refreshBilling,
      openUpgradeModal,
      closeUpgradeModal,
      upgradeModal,
      plan: billing?.plan || null,
      usageBlocked: !!billing?.usageBlocked,
    }),
    [billing, loading, refreshBilling, openUpgradeModal, closeUpgradeModal, upgradeModal],
  );

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan() {
  return useContext(PlanContext);
}

export function handlePlanApiError(errBody, openUpgradeModal) {
  if (!errBody) return false;
  if (errBody.code === "PLAN_LIMIT" || errBody.code === "FEATURE_LOCKED") {
    openUpgradeModal?.(errBody);
    return true;
  }
  if (errBody.code === "USAGE_BLOCKED") {
    openUpgradeModal?.({
      ...errBody,
      blocked: true,
    });
    return true;
  }
  return false;
}
